import { ChevronRight, History } from 'lucide-react';
import { Link } from 'react-router';

import { cx } from '@/lib/cx';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type Lote } from '@fiscoai/contracts';

import { emAndamento, useLotes } from './api';
import { agruparPorDia, resumoDoLote, textoDoResumo, tituloDoLote } from './resumo-lote';
import { PONTO_DO_TOM, STATUS_LOTE } from './rotulos';

/** Quantos envios cabem ao lado do formulário; o resto fica na tela do histórico. */
export const ENVIOS_NO_RESUMO = 6;

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function LinhaLote({ lote }: { lote: Lote }) {
  const status = STATUS_LOTE[lote.status];
  const resumo = resumoDoLote(lote);
  const andando = emAndamento(lote.status);
  return (
    <li>
      <Link
        to={`/importacao/${lote.id}`}
        className="group grid grid-cols-[3rem_minmax(0,1fr)_auto] items-start gap-x-3 rounded-controle px-2 py-2 transition-colors duration-[var(--duracao-rapida)] hover:bg-superficie-hover focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave"
      >
        <span className="pt-px text-xs tabular text-texto-fraco">{hora(lote.criadoEm)}</span>
        <span className="grid min-w-0 gap-0.5">
          <span className="truncate text-sm font-medium">{tituloDoLote(lote)}</span>
          <span
            className={cx(
              'truncate text-xs',
              resumo.problemas > 0 ? 'text-alerta' : 'text-texto-suave',
            )}
          >
            {andando
              ? `${String(lote.processados)} de ${String(lote.totalItens)}…`
              : textoDoResumo(resumo)}
          </span>
        </span>
        <span className="flex items-center gap-1.5 pt-1">
          <span
            title={status.rotulo}
            className={cx(
              'size-2 rounded-pilula',
              PONTO_DO_TOM[status.tom],
              andando && 'animate-pulso',
            )}
          />
          <span className="sr-only">{status.rotulo}</span>
          <ChevronRight
            className="size-4 text-texto-fraco transition-transform duration-[var(--duracao-rapida)] group-hover:translate-x-0.5"
            aria-hidden
          />
        </span>
      </Link>
    </li>
  );
}

/**
 * Os envios mais recentes ao lado do formulário, por dia, com o resultado em uma frase. Preso
 * ao rolar a página; a lista completa fica em /importacao/historico.
 */
export function HistoricoLotes() {
  const consulta = useLotes();
  const todos = consulta.data?.items ?? [];
  const total = consulta.data?.total ?? 0;
  const recentes = todos.slice(0, ENVIOS_NO_RESUMO);
  const temMais = total > ENVIOS_NO_RESUMO;
  return (
    <section
      className="grid content-start gap-3 rounded-painel border border-borda bg-superficie p-4 xl:sticky xl:top-6"
      aria-labelledby="titulo-historico"
    >
      <h2 id="titulo-historico" className="text-base font-semibold">
        Envios recentes
      </h2>
      {consulta.isPending &&
        Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-10 rounded-controle" />)}
      {!consulta.isPending && todos.length === 0 && (
        <EstadoVazio
          icone={<History />}
          titulo="Nenhum envio ainda"
          texto="Os lotes que você importar aparecem aqui, com o resultado de cada arquivo."
          className="py-6"
        />
      )}
      {agruparPorDia(recentes).map((grupo) => (
        <div key={grupo.dia} className="grid gap-1">
          <h3 className="px-2 text-xs font-medium text-texto-fraco">{grupo.dia}</h3>
          <ul className="grid">
            {grupo.lotes.map((lote) => (
              <LinhaLote key={lote.id} lote={lote} />
            ))}
          </ul>
        </div>
      ))}
      {temMais && (
        <Link
          to="/importacao/historico"
          className="justify-self-start rounded-controle px-2 py-1 text-sm font-medium text-marca underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave"
        >
          Ver todo o histórico ({total.toLocaleString('pt-BR')})
        </Link>
      )}
    </section>
  );
}
